import React, { useEffect, useContext, useMemo } from 'react';
import { PromiseState } from 'react-refetch';
import first from 'lodash.first';
import delve from 'dlv';

import getEquivalentsTrendURL from 'url-constructors/getEquivalentsTrendURL';
import getEquivalentsBreakdownURL from 'url-constructors/getEquivalentsBreakdownURL';
import getEquivalentsKPIURL from 'url-constructors/getEquivalentsKPIURL';

import salesTypes from 'maps/sales-types';

import connect from 'helpers/apiConnect';
import { EntityContext } from 'context/entityContext';

import useCombinedRefetchState from 'hooks/useCombinedRefetchState';
import useUrlForTopic from 'components/data-containers/Overview/shared/utils/useUrlForTopic';

import OverviewContainer from 'components/Overview/Container/Container';
import { Grid } from 'components/Overview/Container/shared.style';

import TrendChart from '../shared/TrendChart';
import BreakdownTable from './BreakdownTable';
import OverallPerformanceKPIs from '../shared/KPIsData';
import metrics from 'maps/metrics';
import { TimeFrames } from 'components/TopicFilters/shared';

function dataSources(): any {
  return {
    fetchTrendData: (args: any): any => ({
      trendData: getEquivalentsTrendURL(args),
    }),
    fetchBreakdownData: (args: any): any => ({
      breakdownData: getEquivalentsBreakdownURL(args),
    }),
    fetchKPIData: (args: any): any => ({
      kpiData: getEquivalentsKPIURL(args),
    }),
  };
}

export function getLocalSortBy(globalSortBy: TimeFrames): string {
  switch (globalSortBy) {
    case 'lw':
      return 'count_last_week';
    case 'twtd':
    default:
      return 'count_this_week_to_date';
  }
}

interface IOverallPerformanceProps {
  fetchKPIData: (args: any) => { kpiData: string };
  fetchTrendData: (args: any) => { trendData: string };
  fetchBreakdownData: (args: any) => { breakdownData: string };
  breakdownData: PromiseState<any>;
  kpiData: PromiseState<any>;
  trendData: PromiseState<any>;
  showModal: (value: boolean) => void;
}

/*
 * The overall performance card on overview only accepts the country filter and consists of:
 * - KPIs — this week to date and release to date.
 * - Trend line — album equivalents data over the past 30 days.
 * - Breakdown table — breakdown of the equivalents data.
 *
 * ## External users
 * This container is hidden for external users.
 *
 * ## Differences of the breakdown table per entity type.
 *
 * ### Track
 * - Streaming
 * - Download (so not Download tracks, it's already on a track level.)
 *
 * ### Releases
 * - Streaming
 * - Download (so not Download albums, it's already on a release level and we use 'releases' instead of albums.)
 * - Physical
 *
 * ### Artists and projects
 * - Streaming
 * - Download | Tracks
 * - Download | Releases
 * - Physical
 */
const OverallPerformance: React.FC<IOverallPerformanceProps> = (props) => {
  const {
    fetchTrendData,
    fetchBreakdownData,
    fetchKPIData,
    trendData = PromiseState.create(),
    breakdownData = PromiseState.create(),
    kpiData = PromiseState.create(),
  } = props;

  const ctx = useContext(EntityContext);
  const {
    entityIds,
    entityType,
    compareMode,
    countryIds,
    filters,
    viewSettings,
    customRegionFilter,
  } = ctx;

  const timeframe = viewSettings?.timeframe ?? 'twtd';

  useEffect(() => {
    const args = {
      entityIds,
      entityType,
      countryIds:
        compareMode === 'country' ? countryIds : filters?.country ?? undefined,
      region: filters?.region,
      customRegion: customRegionFilter,
    };

    fetchKPIData(args);

    fetchTrendData({
      ...args,
      period: 'day',
    });

    fetchBreakdownData(args);
  }, [
    compareMode,
    countryIds,
    entityIds,
    entityType,
    fetchKPIData,
    fetchTrendData,
    fetchBreakdownData,
    filters,
    customRegionFilter,
  ]);

  const [, isPending] = useCombinedRefetchState([
    kpiData,
    trendData,
    breakdownData,
  ]);

  const sortByValue = getLocalSortBy(timeframe);
  const sortByMetric = metrics[sortByValue];
  const rtdMetric = metrics['count_rtd'];

  const kpis: any = useMemo(() => {
    if (kpiData.fulfilled) {
      const data = kpiData.value;
      return first(data);
    }
  }, [kpiData]);

  const value = delve(kpis, ['data', sortByMetric.value], 0);
  const change = sortByMetric.change
    ? delve(kpis, ['data', sortByMetric.change], 0)
    : 0;
  const rtdValue = delve(kpis, ['data', rtdMetric.value], 0);

  return (
    <OverviewContainer
      filtersToShow={['country', 'region', 'customRegion']}
      title="Overall Performance"
      subtitle={salesTypes.equivalents.label}
      linkTo={useUrlForTopic('performance/all')}
      short
      glossarySlug={entityType === 'artist' ? '' : 'overview/about-overview'}
    >
      <Grid data-pw="overall-grid-container">
        <div>
          <OverallPerformanceKPIs
            rtdValue={rtdValue}
            value={value}
            change={change}
            title={sortByMetric.label}
            isPending={isPending}
          />
          <TrendChart trendData={trendData} isPending={isPending} />
        </div>
        <BreakdownTable
          breakdownData={breakdownData.value}
          isPending={isPending}
          entityType={entityType}
          activeMetric={sortByMetric}
        />
      </Grid>
    </OverviewContainer>
  );
};

export default connect<IOverallPerformanceProps>(dataSources)(
  OverallPerformance
) as any;
